import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import EncoderAPI from "../../api/EncoderAPI";
import { LIVE_EXTEND_SECONDS } from "../../utils/liveQuota";
import { applyLiveSettings, readLiveSettings } from "../../utils/liveSettings";

import type {
  LiveActionsParams,
  LiveRegieActions,
  LiveReplayRange,
  ReplayRangeMs,
} from "./live.types";
import type { LivePendingAction } from "../../utils/liveView";

function toRangeMs(range: LiveReplayRange): ReplayRangeMs {
  return {
    start: Math.round(range.start * 1000),
    end: Math.round(range.end * 1000),
  };
}

function getServerMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  if (typeof data !== "object" || data === null) return null;
  if ("message" in data && typeof data.message === "string") {
    return data.message || null;
  }
  return null;
}

// The backend error code for a quota refusal is still an open question
// (spec section 11, q.6): a client error whose body mentions "quota" counts.
function isQuotaRefusal(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === undefined || status < 400 || status >= 500) return false;
  return JSON.stringify(error.response?.data ?? "")
    .toLowerCase()
    .includes("quota");
}

export default function useLiveActions({
  item,
  allowReplay,
  canExtend,
  replayUuid,
  setPending,
  setNow,
  setQuotaRefusedAt,
  setReplayOverride,
  setReplayRangeOverride,
  refetchMedia,
  refetchDetails,
  refetchPlan,
  onItemUpdate,
  onSave,
}: LiveActionsParams) {
  const { t } = useTranslation("editor-content");
  const queryClient = useQueryClient();
  const [extendQuotaRefused, setExtendQuotaRefused] = useState(false);

  const planQueryKey = EncoderAPI.generateQuery({
    fn: EncoderAPI.getEncodingPlan,
    params: [],
  }).queryKey;
  const handleError = (error: unknown, quotaRelated = false): void => {
    if (quotaRelated && isQuotaRefusal(error)) {
      setQuotaRefusedAt(
        queryClient.getQueryState(planQueryKey)?.dataUpdatedAt ?? 0,
      );
      refetchPlan();
    }
    toast.error(getServerMessage(error) ?? t("error:genericError"));
  };

  const failPending = (error: unknown, quotaRelated = false): void => {
    setPending(null);
    handleError(error, quotaRelated);
  };

  const createLive = useMutation({
    mutationFn: async () => {
      const { maxResolution, inputType } = readLiveSettings(item);
      return EncoderAPI.createLive({
        mediaId: item.uuid,
        maxResolution,
        inputType,
      });
    },
    onError: (error: unknown) => failPending(error, true),
  });

  const publishLive = useMutation({
    mutationFn: async () => EncoderAPI.publishLive(item.uuid),
    onError: (error: unknown) => failPending(error),
  });

  const stopLive = useMutation({
    mutationFn: async () => EncoderAPI.stopLive(item.uuid),
    onError: (error: unknown) => failPending(error),
  });

  const extendLive = useMutation({
    mutationFn: async () =>
      EncoderAPI.updateLive({ mediaId: item.uuid, time: LIVE_EXTEND_SECONDS }),
    onSuccess: () => {
      refetchMedia();
    },
    onError: (error: unknown) => {
      if (isQuotaRefusal(error)) setExtendQuotaRefused(true);
      handleError(error);
    },
  });

  const createReplay = useMutation({
    mutationFn: async (range: LiveReplayRange) =>
      EncoderAPI.createReplay({
        mediaId: item.uuid,
        replayTimeStart: range.start,
        replayTimeEnd: range.end,
      }),
    onSuccess: (data, range) => {
      setReplayOverride(data.replay_uuid);
      setReplayRangeOverride(toRangeMs(range));
      toast.success(t("createReplaySuccess", { ns: "editor-notif" }));
      refetchMedia();
    },
    onError: (error: unknown) => handleError(error),
  });

  const updateReplay = useMutation({
    mutationFn: async (range: LiveReplayRange) =>
      EncoderAPI.updateReplay({
        replayId: replayUuid ?? "",
        replayTimeStart: range.start,
        replayTimeEnd: range.end,
      }),
    onSuccess: (_data, range) => {
      setReplayRangeOverride(toRangeMs(range));
      toast.success(t("updateReplaySuccess", { ns: "editor-notif" }));
      refetchMedia();
    },
    onError: (error: unknown) => handleError(error),
  });

  // Syncs the clock with the arming instant when the countdown reads it.
  const armPending = (action: LivePendingAction, syncClock = true): void => {
    const since = Date.now();
    if (syncClock) setNow(since);
    setPending({ action, since });
  };

  const actions: LiveRegieActions = {
    start: () => {
      armPending("create");
      createLive.mutate();
    },
    // Re-arms the wait and re-asks the details; never calls createLive again.
    retryProvisioning: () => {
      armPending("create");
      refetchDetails();
    },
    publish: () => {
      armPending("publish", false);
      publishLive.mutate();
    },
    extend: () => {
      if (!canExtend) {
        toast.error(t("notEnoughQuota", { ns: "editor-errors" }));
        return;
      }
      setExtendQuotaRefused(false);
      extendLive.mutate();
    },
    stop: () => {
      armPending("stop");
      stopLive.mutate();
    },
    createReplay: (range) => {
      if (!allowReplay) return;
      createReplay.mutate(range);
    },
    updateReplay: (range) => updateReplay.mutate(range),
    updateSettings: (patch) => {
      const next = applyLiveSettings(item, patch, { allowReplay });
      if (next === item) return;
      setQuotaRefusedAt(null);
      onItemUpdate(next);
    },
    save: () => onSave(),
  };

  return {
    actions,
    extendQuotaRefused,
    isMutating: {
      start: createLive.isLoading,
      publish: publishLive.isLoading,
      stop: stopLive.isLoading,
      extend: extendLive.isLoading,
      createReplay: createReplay.isLoading,
      updateReplay: updateReplay.isLoading,
    } satisfies Partial<Record<keyof LiveRegieActions, boolean>>,
  };
}
